import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { authAPI } from '../../services/api';
import { useTimeOfDay } from '../../hooks/useTimeOfDay';
import './Auth.css';

function PasswordResetConfirm() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const timeOfDay = useTimeOfDay();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const backgroundImage = timeOfDay === 'morning'
    ? 'url(/loginmorn.png)'
    : 'url(/loginday.jpg)';

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await authAPI.confirmPasswordReset(token, password);
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="auth-page" style={{ backgroundImage }}>
        <div className="auth-card">
          <div className="auth-header">
            <img src="/logo.png" alt="WFMZ" className="auth-logo" />
            <h1 className="auth-title">Invalid Link</h1>
            <p className="auth-subtitle">
              This password reset link is invalid or has expired.
            </p>
          </div>
          <div className="auth-footer">
            <Link to="/reset-password">Request a new reset link</Link>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="auth-page" style={{ backgroundImage }}>
        <div className="auth-card">
          <div className="auth-header">
            <img src="/logo.png" alt="WFMZ" className="auth-logo" />
            <h1 className="auth-title">Password Reset</h1>
            <p className="auth-subtitle">
              Your password has been reset successfully. Redirecting to login...
            </p>
          </div>
          <div className="auth-footer">
            <Link to="/login">Go to Sign In</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page" style={{ backgroundImage }}>
      <div className="auth-card">
        <div className="auth-header">
          <img src="/logo.png" alt="WFMZ" className="auth-logo" />
          <h1 className="auth-title">Set New Password</h1>
          <p className="auth-subtitle">
            Enter your new password below.
          </p>
        </div>

        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="password" className="form-label">
              New Password
            </label>
            <input
              id="password"
              type="password"
              className="form-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter new password"
              autoComplete="new-password"
              minLength={8}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword" className="form-label">
              Confirm Password
            </label>
            <input
              id="confirmPassword"
              type="password"
              className="form-input"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              autoComplete="new-password"
              minLength={8}
              required
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-lg auth-submit"
            disabled={loading || !password || !confirmPassword}
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>

        <div className="auth-footer">
          <Link to="/login">Back to Sign In</Link>
        </div>
      </div>
    </div>
  );
}

export default PasswordResetConfirm;
